import { Theme } from '@emotion/react';
import { Box, IconButton, SxProps } from '@mui/material';
import fundOpportunityContentCache from 'FundOpportunityContentCache';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ContentManagementInterface from '../models/ContentManagementInterface';
import FundOpportunity from '../models/FundOpportunity';
import FundOpportunityCardData from '../models/FundOpportunityCardData';
import Investor from '../models/Investor';
import PropertyCard from './PropertyCard';
import { Palette } from 'Palette';
import { CARD_GRID_LAYOUT, DESCRIPTION_BOTTOM_PADDING, TITLE_BOTTOM_PADDING } from 'DesignTokens';
import { ArrowIcon, StyledSvg } from './styles/StyledCardLink';
import StyledSection from './styles/StyledSection';
import { StyledHeading } from './styles/styles';
import { useCardRowAlignment } from './useCardRowAlignment';

interface PropertyCardGroupProps {
  investors?: Investor[];
  fundOpportunities?: FundOpportunity[];
  sourceType?: 'investor' | 'fundOpportunity';
  title?: string;
  subTitle?: string;
  subtitleVariable?: React.ComponentProps<typeof StyledHeading>['headerVariant'];
  description?: string;
  /** Rendered inline, right-aligned against the main title (e.g. a page-level "Create" action). */
  headerAction?: React.ReactNode;
  tone?: keyof typeof Palette;
  propertyCardTone?: keyof typeof Palette;
  noSpacing?: boolean;
  linkable?: boolean;
  routePrefix?: string;
  children?: React.ReactNode;
  contentAware?: boolean;
  linkTo?: 'update' | 'details';
  preventFullSizePopup?: boolean;
  noTopPadding?: boolean;
  noTopMargin?: boolean;
  /**
   * This group is the first/only thing in its page or tab panel, so it
   * shouldn't carry the top spacing it would need if stacked below a
   * sibling — equivalent to passing noTopPadding and noTopMargin together,
   * named for the one thing every caller actually means by that pair.
   */
  firstElement?: boolean;
  reduceTitleTopPadding?: boolean;
  footnotes?: React.ReactNode;
  verticalStack?: boolean;
  mini?: boolean;
  sx?: SxProps<Theme>;
  showExpandAll?: boolean; // New prop for showing Expand All button
  forceExpanded?: boolean; // New prop to force all cards to be expanded via group control
  hasUnreadUpdates?: (fundOpportunityId: string) => boolean; // Function to check if fund opportunity has unread updates
  getAllocationBreakdown?: (
    fundOpportunityId: string,
  ) => Array<{ entityName: string; amount: string }> | null;
}

const PropertyCardGroup: React.FC<PropertyCardGroupProps> = ({
  investors,
  fundOpportunities,
  sourceType = 'investor',
  title,
  description,
  headerAction,
  tone,
  propertyCardTone,
  noSpacing,
  linkable,
  routePrefix,
  children,
  contentAware,
  linkTo,
  preventFullSizePopup,
  noTopPadding,
  noTopMargin,
  firstElement,
  reduceTitleTopPadding,
  subTitle,
  subtitleVariable = 'heading2',
  footnotes,
  mini = false,
  sx,
  showExpandAll = false, // Default to false
  forceExpanded, // New prop to control expansion from parent
  hasUnreadUpdates,
  getAllocationBreakdown,
}) => {
  const resolvedTone = tone || 'tone1';
  const resolvedCardTone = propertyCardTone ?? 'tone2';
  const [allExpanded, setAllExpanded] = useState(false); // State for expand all functionality
  const [cardsWithTruncatedText, setCardsWithTruncatedText] = useState<Set<string>>(new Set()); // Track which cards have truncated text
  const cardRowRef = useRef<HTMLDivElement>(null);

  // Callback to handle truncation state updates from individual cards
  const handleCardTruncationChange = (cardId: string, hasTruncatedText: boolean) => {
    setCardsWithTruncatedText((prev) => {
      const newSet = new Set(prev);
      if (hasTruncatedText) {
        newSet.add(cardId);
      } else {
        newSet.delete(cardId);
      }
      return newSet;
    });
  };

  const renderTitle = () => {
    const titleHeading = (
      <StyledHeading
        tone={resolvedTone}
        fullTopSpacing={!reduceTitleTopPadding}
        // A section without a description would otherwise lose the gap
        // renderDescription's own padding provides before the cards —
        // carry it on the title instead so the gap above the cards stays
        // the same whether or not a section has a description.
        sx={!description && TITLE_BOTTOM_PADDING ? { pb: TITLE_BOTTOM_PADDING } : undefined}
      >
        {title}
      </StyledHeading>
    );
    return (
      <>
        {headerAction ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {titleHeading}
            <Box sx={{ mt: '5px' }}>{headerAction}</Box>
          </Box>
        ) : (
          titleHeading
        )}
        {subTitle && (
          <StyledHeading headerVariant={subtitleVariable} tone={resolvedTone}>
            {subTitle}
          </StyledHeading>
        )}
      </>
    );
  };

  const renderDescription = () => {
    return (
      <StyledHeading
        headerVariant="subheading"
        tone={resolvedTone}
        sx={{ pb: DESCRIPTION_BOTTOM_PADDING }}
      >
        {description}
      </StyledHeading>
    );
  };

  const [content, setContent] = useState<{
    [key: string]: ContentManagementInterface;
  }>({});

  const items = useMemo(
    () => (sourceType === 'investor' ? (investors ?? []) : (fundOpportunities ?? [])),
    [sourceType, investors, fundOpportunities],
  );

  useEffect(() => {
    if (!contentAware) {
      return;
    }

    const promises = items.map((item) => {
      const fundOpp =
        sourceType === 'investor' ? (item as Investor).fundOpportunity : (item as FundOpportunity);

      const id = fundOpp?.id;
      if (!id || content[id]) return Promise.resolve();

      return fundOpportunityContentCache
        .getContent(id)
        .then((fetchedContent: ContentManagementInterface | null) => {
          setContent((prev) => ({
            ...prev,
            [id]: fetchedContent as ContentManagementInterface,
          }));
        });
    });

    Promise.all(promises).catch((err) => console.error('Error loading content:', err));
  }, [contentAware, content, items, sourceType]);

  const cardItems = children
    ? React.Children.map(children, (child) => {
        // Clone child element and add forceExpanded prop if it's a PropertyCard and showExpandAll is enabled
        if (React.isValidElement(child) && typeof child.type !== 'string') {
          const childProps = {
            ...child.props,
          };

          // Only pass forceExpanded when showExpandAll is enabled (group expansion mode)
          if (showExpandAll) {
            childProps.forceExpanded = forceExpanded !== undefined ? forceExpanded : allExpanded;
          }

          // Inject onTruncationChange callback for PropertyCard components
          if (child.type === PropertyCard) {
            const originalOnTruncationChange = childProps.onTruncationChange;
            childProps.onTruncationChange = (hasTruncatedText: boolean) => {
              // Call the original callback if it exists
              originalOnTruncationChange?.(hasTruncatedText);
              // Also report to the group
              const cardId =
                childProps.cardData?.fundOpportunity?.id ||
                childProps.title ||
                `card-${Math.random()}`;
              handleCardTruncationChange(cardId, hasTruncatedText);
            };
          }

          return React.cloneElement(child as React.ReactElement<any>, childProps);
        }
        return child;
      }) || []
    : items.map((item) => {
        const fundOpp =
          sourceType === 'investor'
            ? (item as Investor).fundOpportunity!
            : (item as FundOpportunity);
        const investor =
          sourceType === 'investor'
            ? (item as Investor)
            : ({ fundOpportunity: fundOpp } as Investor);

        const cardData = new FundOpportunityCardData(
          fundOpp,
          investor,
          !contentAware || !!content[fundOpp.id],
          content[fundOpp.id],
        );

        const evaluatedLinkTo =
          sourceType === 'investor' &&
          (investor.fundOpportunity?.status === 'UNDER_MANAGEMENT' ||
            investor.fundOpportunity?.status === 'COMPLETE') &&
          investor.status === 'COMMITTED_INVESTOR'
            ? 'update'
            : 'details';

        const contentVersion = Object.keys(content).includes(fundOpp.id);

        return (
          <PropertyCard
            key={`${fundOpp.id}-${contentVersion}`}
            cardData={cardData}
            linkTo={linkTo || evaluatedLinkTo}
            tone={resolvedCardTone}
            linkable={linkable}
            routePrefix={routePrefix}
            isHtmlDescription={contentAware}
            preventFullSizePopup={preventFullSizePopup}
            mini={mini}
            forceExpanded={
              showExpandAll
                ? forceExpanded !== undefined
                  ? forceExpanded
                  : allExpanded
                : undefined
            } // Only use group expansion when showExpandAll is enabled
            onExpand={() => {
              // Handle individual card expansion if needed
            }}
            onTruncationChange={(hasTruncatedText: boolean) => {
              handleCardTruncationChange(fundOpp.id, hasTruncatedText);
            }}
            hasUnreadUpdates={hasUnreadUpdates?.(fundOpp.id) || false}
            allocationBreakdown={getAllocationBreakdown?.(fundOpp.id)}
          />
        );
      });

  useCardRowAlignment(cardRowRef, cardItems.length);

  return (
    <StyledSection
      tone={resolvedTone}
      sx={{
        my: noSpacing || footnotes ? 0 : 4,
        mt: firstElement || noTopMargin ? 0 : undefined,
        pt: noSpacing || firstElement || noTopPadding ? 0 : undefined,
        px: footnotes ? 0 : undefined,
        boxSizing: 'border-box',
        ...sx,
      }}
      additionalClassName="react-PropertyCardGroup"
    >
      {title && renderTitle()}
      {description && renderDescription()}

      <Box
        ref={cardRowRef}
        sx={{
          width: '100%',
          ...(CARD_GRID_LAYOUT
            ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 360px))',
                gap: '1.875rem',
                justifyContent: { xs: 'center', lg: 'start' },
              }
            : {
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: '1.875rem', // 30px horizontal spacing
                rowGap: '1.875rem', // 30px vertical spacing
                justifyContent: { xs: 'center', lg: 'flex-start' },
              }),
        }}
      >
        {cardItems || []}
      </Box>

      {/* Expand All Button - only shown for non-linkable cards when there are cards with truncated text */}
      {showExpandAll && !linkable && cardsWithTruncatedText.size > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            aria-label={allExpanded ? 'Collapse all' : 'Expand all'}
            onClick={() => setAllExpanded(!allExpanded)}
            sx={{
              p: 0,
              backgroundColor: 'transparent',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: allExpanded ? 'rotate(-90deg)' : 'rotate(90deg)',
                transition: 'transform 0.2s ease-in-out',
              }}
            >
              <ArrowIcon style={{ paddingRight: '0.3125rem' }}>
                <StyledSvg />
              </ArrowIcon>
            </Box>
          </IconButton>
        </Box>
      )}

      {footnotes}
    </StyledSection>
  );
};

export default PropertyCardGroup;
